import { Event } from "./types/events";
import { Resource } from "./types/resources";

const getEventId = (event: Event): string => String(event.id);

const getTime = (date: Date | number): number =>
  date instanceof Date ? date.getTime() : new Date(date).getTime();

const normalizeResourceIds = (
  event: Event,
  getEventResourceIds: (event: Event) => (string | number)[]
): string[] =>
  (getEventResourceIds(event) || [])
    .map((resourceId) => String(resourceId))
    .sort();

export const getOptimisticDroppedEvent = (
  event: Event,
  newStartTime: Date,
  newResource?: Resource
): Event => {
  const currentStart = new Date(event.timeRange.start);
  const currentEnd = new Date(event.timeRange.end);
  const duration = currentEnd.getTime() - currentStart.getTime();

  return {
    ...event,
    assignedResources: newResource
      ? [String(newResource.id)]
      : event.assignedResources,
    timeRange: {
      start: newStartTime,
      end: new Date(newStartTime.getTime() + duration),
    },
  } as Event;
};

export const mergeOptimisticEvents = (
  events: Event[],
  optimisticEvents: Map<string, Event>
): Event[] => {
  if (!optimisticEvents.size) return events;

  return events.map((event) => optimisticEvents.get(getEventId(event)) || event);
};

export const reconcileOptimisticEvents = (
  sourceEvents: Event[],
  optimisticEvents: Map<string, Event>,
  getEventResourceIds: (event: Event) => (string | number)[]
): Map<string, Event> => {
  if (!optimisticEvents.size) return optimisticEvents;

  const sourceEventsById = new Map(
    sourceEvents.map((event) => [getEventId(event), event])
  );
  const nextOptimisticEvents = new Map(optimisticEvents);

  optimisticEvents.forEach((optimisticEvent, eventId) => {
    const sourceEvent = sourceEventsById.get(eventId);
    if (!sourceEvent) {
      nextOptimisticEvents.delete(eventId);
      return;
    }

    const sourceResourceIds = normalizeResourceIds(sourceEvent, getEventResourceIds);
    const optimisticResourceIds = normalizeResourceIds(
      optimisticEvent,
      getEventResourceIds
    );

    const hasMatchingTimeRange =
      getTime(sourceEvent.timeRange.start) ===
        getTime(optimisticEvent.timeRange.start) &&
      getTime(sourceEvent.timeRange.end) ===
        getTime(optimisticEvent.timeRange.end);
    const hasMatchingResources =
      sourceResourceIds.length === optimisticResourceIds.length &&
      sourceResourceIds.every(
        (resourceId, index) => resourceId === optimisticResourceIds[index]
      );

    if (hasMatchingTimeRange && hasMatchingResources) {
      nextOptimisticEvents.delete(eventId);
    }
  });

  return nextOptimisticEvents;
};
